import { Button, ButtonGroup, Checkbox, Field, NumericInput, RadioGroup, TextOutput } from 'Components/Core';
import { withInjectedProps } from 'Components/Core/InjectedPropsContext';
import { FieldSet, ListFieldSection } from 'Components/Layout';
import {
  IWorksheetComponent,
  WorksheetAssociatedTestSection,
  WorksheetBalanceFieldSet,
  WorksheetEquipmentDropdown,
} from 'Components/Worksheets/Core';
import { StabilizedMaterial } from 'Components/Worksheets/Core/Fields/StabilizedMaterial';
import { MaterialDriedByType } from 'Components/Worksheets/Shared/types';
import { hasValue, valueOrDefault } from 'Lib/Any';
import { pipe } from 'Lib/Hooks/Selectors';
import deepEquals from 'Lib/Object/deepEquals';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { TestLocation } from '../types';

export interface IPreparationSectionProps extends IWorksheetComponent {
  hideNoneEmbeddedTestFields?: boolean;
}

let PreparationSection: React.FC<IPreparationSectionProps> = ({
  createWorksheetSelector,
  hideNoneEmbeddedTestFields = false,
}) => {
  const [testConditionsEditable, setTestConditionsEditable] = useState(false);

  const samplePreparationMethod = useSelector(
    createWorksheetSelector!('Preparation.SamplePreparationMethod'),
    deepEquals
  ) as MaterialDriedByType;

  const isFieldTestLocation = useSelector(
    pipe(createWorksheetSelector!('Preparation.TestLocation'), (testLocation: TestLocation) => {
      return testLocation === TestLocation.Field;
    })
  );

  const testLocationOptions: Array<{ label: string; value: TestLocation }> = [
    { label: 'Laboratory', value: TestLocation.Laboratory },
    { label: 'Field', value: TestLocation.Field },
  ];

  const samplePreparationOptions: Array<{ label: string; value: MaterialDriedByType }> = [
    { label: 'Natural', value: MaterialDriedByType.Natural },
    { label: 'Air Dried', value: MaterialDriedByType.Air },
    { label: 'Oven Dried (50°C)', value: MaterialDriedByType.Oven },
  ];

  const isNoOversizeMaterial = useSelector(
    pipe(createWorksheetSelector!('Preparation.NoOversizeMaterial'), valueOrDefault(false))
  );

  const massRetainedOn20_0mmSieveHasValue = useSelector(
    pipe(createWorksheetSelector!('Preparation.MassRetainedOn20_0mmSieve'), hasValue)
  );

  return (
    <>
      <ListFieldSection>
        <FieldSet>
          <Field input={<NumericInput />} name="Preparation.DropHeight" visible={testConditionsEditable} />
          <Field input={<TextOutput />} name="Preparation.DropHeight" visible={!testConditionsEditable} />
          <StabilizedMaterial
            visible={!testConditionsEditable && !hideNoneEmbeddedTestFields}
            appendNamePrefix="Preparation"
          />
          <ListFieldSection visible={!testConditionsEditable}>
            <ButtonGroup>
              <Button
                onClick={() => {
                  setTestConditionsEditable(true);
                }}
              >
                Change
              </Button>
            </ButtonGroup>
          </ListFieldSection>
        </FieldSet>
        <Field
          input={<TextOutput />}
          name="Preparation.SamplePreparationMethod"
          value={samplePreparationOptions.find((o) => o.value === samplePreparationMethod)?.label}
          visible={(!testConditionsEditable || isFieldTestLocation) && !hideNoneEmbeddedTestFields}
        />
        <Field
          input={<RadioGroup />}
          name="Preparation.SamplePreparationMethod"
          options={samplePreparationOptions}
          visible={testConditionsEditable && !isFieldTestLocation && !hideNoneEmbeddedTestFields}
        />
        <StabilizedMaterial
          editable
          visible={testConditionsEditable && !hideNoneEmbeddedTestFields}
          appendNamePrefix="Preparation"
        />
      </ListFieldSection>

      <ListFieldSection visible={!hideNoneEmbeddedTestFields}>
        <Field
          input={<WorksheetEquipmentDropdown />}
          equipmentType="oven"
          name="Preparation.Oven"
          visible={samplePreparationMethod === MaterialDriedByType.Oven}
        />

        <Field input={<RadioGroup />} name="Preparation.TestLocation" options={testLocationOptions} />

        <WorksheetBalanceFieldSet name="Preparation.Balance" />

        <Field input={<Checkbox />} name="Preparation.BalanceIsSuitableForUse" visible={isFieldTestLocation} />
        <Field
          input={<Checkbox />}
          name="Preparation.NoOversizeMaterial"
          confirm={massRetainedOn20_0mmSieveHasValue && !isNoOversizeMaterial}
          confirmTitle="Confirm No Oversize Material check"
          confirmText="This will clear the mass retained on the 20mm sieve and cannot be undone, continue?"
          confirmButtonText="Confirm"
        />

        <Field
          input={<WorksheetEquipmentDropdown />}
          equipmentType="sieve"
          name="Preparation.Sieve"
          queryParams={{ SieveSize: '0.020 m' }}
          visible={!isNoOversizeMaterial}
        />

        <Field input={<NumericInput />} name="Preparation.TareMass" />
        <Field input={<NumericInput />} name="Preparation.TareAndTotalSampleMass" />
        <Field input={<TextOutput />} name="Preparation.TotalSampleMass" />
        <Field input={<NumericInput />} name="Preparation.MassRetainedOn20_0mmSieve" visible={!isNoOversizeMaterial} />
        <Field input={<TextOutput />} name="Preparation.MassRetainedOn20_0mmSievePercentage" />
        <Field input={<NumericInput />} name="Preparation.TestPortionMass" />
      </ListFieldSection>

      <ListFieldSection heading={hideNoneEmbeddedTestFields ? '' : 'Passing 20mm As-Received Water Content'} wide>
        <WorksheetAssociatedTestSection
          name="Preparation.EmbeddedWaterContentTest"
          testType="Moisture Content"
          heading="Water Content BS EN ISO 17892-1"
          associatedTestModel={{
            measured: true,
          }}
        />
      </ListFieldSection>
      <ListFieldSection visible={!hideNoneEmbeddedTestFields}>
        <Field input={<TextOutput />} name="Preparation.TotalSampleDryMass" />
        <Field input={<TextOutput />} name="Preparation.DryMassRetainedOn20_0mmSievePercentage" />
      </ListFieldSection>
    </>
  );
};

PreparationSection = withInjectedProps(PreparationSection);

export { PreparationSection };
export default PreparationSection;
